import type {
  AiRecommendation,
  BeadDesign,
  BeadPalette,
  CellColorId,
  GridSize,
  MaxColors,
  PatternSettings
} from '../types';
import { t, type Locale } from '../i18n';
import { findNearestPaletteColor } from './color';

const TRANSPARENT = 'transparent';

export function createDesignId(prefix = 'des') {
  if (crypto.randomUUID) {
    return `${prefix}_${crypto.randomUUID().slice(0, 8)}`;
  }
  return `${prefix}_${Date.now().toString(36)}`;
}

export function countCells(cells: CellColorId[]) {
  return cells.reduce<Record<string, number>>((counts, colorId) => {
    if (colorId === TRANSPARENT) return counts;
    counts[colorId] = (counts[colorId] ?? 0) + 1;
    return counts;
  }, {});
}

export function totalBeads(counts: Record<string, number>) {
  return Object.values(counts).reduce((total, count) => total + count, 0);
}

export function usedColorCount(counts: Record<string, number>) {
  return Object.keys(counts).length;
}

export function validateGrid(cells: CellColorId[], width: number, height: number, palette: BeadPalette) {
  const validIds = new Set(palette.colors.map((color) => color.id));
  return cells.length === width * height && cells.every((cell) => validIds.has(cell));
}

export function makeDesign(input: {
  title: string;
  width: number;
  height: number;
  paletteId: string;
  cells: CellColorId[];
  settings: PatternSettings;
  ownerId?: string | null;
  assets?: BeadDesign['assets'];
  id?: string;
  createdAt?: string;
}): BeadDesign {
  const now = new Date().toISOString();
  return {
    schemaVersion: 1,
    id: input.id ?? createDesignId(),
    ownerId: input.ownerId ?? null,
    title: input.title,
    width: input.width,
    height: input.height,
    paletteId: input.paletteId,
    cells: input.cells,
    counts: countCells(input.cells),
    settings: input.settings,
    assets: input.assets ?? {},
    visibility: 'private',
    createdAt: input.createdAt ?? now,
    updatedAt: now
  };
}

export function createBlankDesign(settings: PatternSettings, palette: BeadPalette, title = 'Untitled bead pattern') {
  const cells = Array.from<CellColorId>({ length: settings.gridSize * settings.gridSize }).fill(TRANSPARENT);
  return makeDesign({
    title,
    width: settings.gridSize,
    height: settings.gridSize,
    paletteId: palette.id,
    cells,
    settings: { ...settings, sourceType: 'blank' }
  });
}

function getImageDimensions(image: HTMLImageElement | ImageBitmap) {
  return {
    width: image instanceof HTMLImageElement ? image.naturalWidth || image.width : image.width,
    height: image instanceof HTMLImageElement ? image.naturalHeight || image.height : image.height
  };
}

function reduceMappedColors(cells: CellColorId[], palette: BeadPalette, maxColors: MaxColors) {
  const counts = countCells(cells);
  const selected = new Set(
    Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, maxColors)
      .map(([colorId]) => colorId)
  );

  if (selected.size >= Object.keys(counts).length) return cells;

  const allowedColors = palette.colors.filter((color) => selected.has(color.id));

  return cells.map((cell) => {
    if (cell === TRANSPARENT || selected.has(cell)) return cell;
    const original = palette.colors.find((color) => color.id === cell);
    if (!original) return TRANSPARENT;
    return findNearestPaletteColor(hexToRgbSafe(original.hex), allowedColors).id;
  });
}

function hexToRgbSafe(hex: string) {
  const value = Number.parseInt(hex.replace('#', ''), 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255
  };
}

export async function loadImage(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);

  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });
}

export function generatePatternFromImage(
  image: HTMLImageElement | ImageBitmap,
  palette: BeadPalette,
  settings: PatternSettings
) {
  const width = settings.gridSize;
  const height = settings.gridSize;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('Canvas 2D context is unavailable.');

  if (settings.backgroundMode === 'solid') {
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
  } else {
    context.clearRect(0, 0, width, height);
  }

  const imageSize = getImageDimensions(image);
  const radians = (settings.rotation * Math.PI) / 180;
  const rotated = Math.abs(settings.rotation % 180) === 90;
  const sourceW = rotated ? imageSize.height : imageSize.width;
  const sourceH = rotated ? imageSize.width : imageSize.height;
  const scale = Math.max(width / sourceW, height / sourceH) * settings.zoom;
  const drawW = imageSize.width * scale;
  const drawH = imageSize.height * scale;

  context.save();
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.translate(width / 2, height / 2);
  context.rotate(radians);
  context.drawImage(image, -drawW / 2, -drawH / 2, drawW, drawH);
  context.restore();

  const imageData = context.getImageData(0, 0, width, height);
  const mapped: CellColorId[] = [];
  const colors = palette.colors.filter((color) => color.id !== TRANSPARENT);

  for (let index = 0; index < imageData.data.length; index += 4) {
    const r = imageData.data[index];
    const g = imageData.data[index + 1];
    const b = imageData.data[index + 2];
    const a = imageData.data[index + 3];

    if (a < settings.transparentAlphaThreshold && settings.backgroundMode === 'transparent') {
      mapped.push(TRANSPARENT);
      continue;
    }

    const composited =
      a < 255 && settings.backgroundMode !== 'transparent'
        ? {
            r: Math.round((r * a + 255 * (255 - a)) / 255),
            g: Math.round((g * a + 255 * (255 - a)) / 255),
            b: Math.round((b * a + 255 * (255 - a)) / 255)
          }
        : { r, g, b };

    mapped.push(findNearestPaletteColor(composited, colors).id);
  }

  const reduced = reduceMappedColors(mapped, palette, settings.maxColors);
  return settings.cleanup.removeIsolatedPixels
    ? removeIsolatedPixels(reduced, width, height)
    : reduced;
}

export function removeIsolatedPixels(cells: CellColorId[], width: number, height: number) {
  const next = [...cells];

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = y * width + x;
      const current = cells[index];
      if (current === TRANSPARENT) continue;

      const neighbors = new Map<string, number>();
      for (let ny = Math.max(0, y - 1); ny <= Math.min(height - 1, y + 1); ny += 1) {
        for (let nx = Math.max(0, x - 1); nx <= Math.min(width - 1, x + 1); nx += 1) {
          if (nx === x && ny === y) continue;
          const id = cells[ny * width + nx];
          if (id === TRANSPARENT) continue;
          neighbors.set(id, (neighbors.get(id) ?? 0) + 1);
        }
      }

      const dominant = [...neighbors.entries()].sort((a, b) => b[1] - a[1])[0];
      if (dominant && dominant[0] !== current && dominant[1] >= 5) {
        next[index] = dominant[0];
      }
    }
  }

  return next;
}

export function replaceColor(cells: CellColorId[], from: CellColorId, to: CellColorId) {
  if (from === to) return cells;
  return cells.map((cell) => (cell === from ? to : cell));
}

export function floodFill(cells: CellColorId[], width: number, height: number, startIndex: number, colorId: CellColorId) {
  const target = cells[startIndex];
  if (target === colorId) return cells;

  const next = [...cells];
  const queue = [startIndex];
  const visited = new Set<number>();

  while (queue.length) {
    const index = queue.shift()!;
    if (visited.has(index) || next[index] !== target) continue;
    visited.add(index);
    next[index] = colorId;

    const x = index % width;
    const y = Math.floor(index / width);
    if (x > 0) queue.push(index - 1);
    if (x < width - 1) queue.push(index + 1);
    if (y > 0) queue.push(index - width);
    if (y < height - 1) queue.push(index + width);
  }

  return next;
}

export function makeLocalRecommendation(
  image: HTMLImageElement | null,
  currentSettings: PatternSettings,
  locale: Locale = 'en'
): AiRecommendation {
  const pixels = image ? (image.naturalWidth || image.width) * (image.naturalHeight || image.height) : 0;
  const veryLarge = pixels > 2_500_000;
  const medium = pixels > 700_000;
  const recommendedSize: GridSize = veryLarge ? 100 : medium ? 80 : 58;
  const recommendedColors: MaxColors = veryLarge ? 24 : medium ? 16 : 12;

  return {
    subject: image ? t(locale, 'aiSubjectUpload') : t(locale, 'aiSubjectTemplate'),
    image_complexity: veryLarge ? 'high' : medium ? 'medium' : 'low',
    recommended_grid: {
      width: recommendedSize,
      height: recommendedSize,
      reason: veryLarge
        ? t(locale, 'aiReasonLarge')
        : t(locale, 'aiReasonBalanced')
    },
    recommended_color_count: {
      count: recommendedColors,
      reason:
        recommendedColors > currentSettings.maxColors
          ? t(locale, 'aiColorReasonMore')
          : t(locale, 'aiColorReasonCurrent')
    },
    background_recommendation: image ? 'transparent' : currentSettings.backgroundMode,
    important_features: [t(locale, 'aiFeatureSilhouette'), t(locale, 'aiFeatureEdges'), t(locale, 'aiFeatureDetails')],
    simplification_strategy: [t(locale, 'aiStrategyCrop'), t(locale, 'aiStrategyMerge'), t(locale, 'aiStrategyPalette')],
    difficulty: veryLarge ? 'hard' : medium ? 'medium' : 'easy',
    warnings: veryLarge ? [t(locale, 'aiWarningLarge')] : [],
    source: 'local_heuristic'
  };
}
